import * as Logger from "js-logger";
import { Signal } from "signals";
import Components from "slotworks/components/components";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import AbstractSymbolBehaviour, { DefaultSymbolBehaviour } from "slotworks/components/matrix/symbol/behaviours/abstract-symbol-behaviour";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import Layers from "slotworks/graphics/layers/layers";
import { AnticipationResult } from "slotworks/model/gameplay/records/results/anticipation-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import SlotDefinition from "slotworks/model/slot-definition";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import Timer from "slotworks/utils/timer";

class MatrixComponent {

    public readonly startSignal: Signal;
    public readonly stopSignal: Signal;
    public readonly landSignal: Signal;
    public readonly skipSignal: Signal;
    public readonly onAnticipationStart: Signal;
    public readonly onAnticipationEnd: Signal;

    // z-sort symbols based on game-config zIndex. Will ignore other items on matrixLayer
    public zSortEnabled: boolean = false;

    // By default symbols z-sort right-to-left, bottom-to-top
    public zSortLeftToRight: boolean = false;
    public zSortTopToBottom: boolean = false;

    public stickyLayer: Layers;

    private defaultSymbolBehaviourTypes: DefaultSymbolBehaviour[] = [];

    private reelTransition: AbstractReelTransition;
    private reels: ReelComponent[];

    private stuckSymbols: Map<string, SymbolComponent> = new Map<string, SymbolComponent>();

    /**
     * Array of symbols used only for transitions (ie reel spins)
     */
    private hiddenSymbols: SymbolComponent[];

    private defaultStops: number[];
    private defaultReelset: string[][];

    private currentStops: number[];
    private currentReelset: string[][];

    private updateTimer: number;

    private matrixLayer: Layers;
    private animationLayer: Layers;

    constructor(grid: number[], stops: number[], reelset: string[][], matrixLayer?: Layers, animationLayer?: Layers) {

        this.matrixLayer = matrixLayer || Layers.MatrixContent;
        this.animationLayer = animationLayer || Layers.SymbolAnimations;

        this.startSignal = new Signal();
        this.stopSignal = new Signal();
        this.landSignal = new Signal();
        this.skipSignal = new Signal();

        this.matrixLayer.enableBoundsMask(true);
        this.onAnticipationStart = new Signal();
        this.onAnticipationEnd = new Signal();

        this.change("default", grid, stops, reelset);

        this.updateTimer = Timer.setInterval(this.update, 0);
    }

    public addDefaultSymbolBehaviour(behaviourType: typeof AbstractSymbolBehaviour, filteredSymbols: string[]) {
        const defaultSymbolBehaviour = { behaviourType, filteredSymbols };
        this.defaultSymbolBehaviourTypes.push(defaultSymbolBehaviour);

        for (const symbol of this.getAllSymbols()) {
            const behaviour = new defaultSymbolBehaviour.behaviourType(symbol);
            behaviour.validSymbols = defaultSymbolBehaviour.filteredSymbols;

            symbol.addBehaviour(behaviour);
            symbol.refresh();
        }
    }

    public reset() {
        this.jump(this.currentStops, this.currentReelset);
        this.postProcessReels();
    }

    // Force an update outside of the game loop tick
    public flush() {
        this.postProcessReels();
    }

    public getTransition() {
        return this.reelTransition;
    }

    public setTransition(reelTransition: AbstractReelTransition) {
        this.reelTransition = reelTransition;
        this.reelTransition.init(this.reels, this.defaultStops, this.defaultReelset);
        this.reelTransition.onReelLand.add(this.onReelLand);
        this.reelTransition.onAnticipationStart.add(() => {
            this.onAnticipationStart.dispatch();
        });
        this.reelTransition.onAnticipationEnd.add(() => {
            this.onAnticipationEnd.dispatch();
        });
    }

    public startTransition(jumpStart: boolean = false, quickSpin: boolean = false): Contract<void> {
        for (const reel of this.reels) {
            reel.spin().execute();
        }

        this.startSignal.dispatch();

        return this.reelTransition.start(null, jumpStart, quickSpin);
    }

    public stopTransition(spinRecord: SpinRecord, quickSpin: boolean = false): Contract<void> {
        return new Contract<void>((resolve) => {
            this.currentStops = spinRecord.stops;
            this.currentReelset = spinRecord.reelset;

            const anticipatations: AnticipationResult[] = spinRecord.getResultsOfType(AnticipationResult);
            const anticipationResult: AnticipationResult = anticipatations[0];

            for (let reel = 0; reel < this.reels.length; reel++) {
                if (anticipationResult && anticipationResult.landSymbols) {
                    this.reels[reel].validLandSymbols = anticipationResult.landSymbols[reel];
                }
            }

            this.reelTransition.stop(spinRecord, quickSpin).then(() => {
                this.postProcessReels();
                resolve(null);
                this.stopSignal.dispatch();
            });
        });
    }

    public skipTransition(spinRecord: SpinRecord): Contract<void> {
        return new Contract<void>((resolve) => {
            this.skipSignal.dispatch();

            this.currentStops = spinRecord.stops;
            this.currentReelset = spinRecord.reelset;

            const anticipations: AnticipationResult[] = spinRecord.getResultsOfType(AnticipationResult);
            const anticipation: AnticipationResult = anticipations[0];
            if (anticipation) {
                for (let reel = 0; reel < this.reels.length; reel++) {
                    this.reels[reel].validLandSymbols = anticipation.landSymbols[reel];
                }
            }

            this.reelTransition.skip(spinRecord).then(() => {
                this.postProcessReels();
                this.stopSignal.dispatch();
                resolve(null);
            });
        });
    }

    public jumpToGrid(grid: string[][]) {
        const stops = [];
        const reelset: string[][] = [];
        for (let reelIndex = 0; reelIndex < grid.length; reelIndex++) {
            const reel = grid[reelIndex];
            stops.push(0);
            reelset.push([...reel, ...this.currentReelset[reelIndex]]);
        }

        this.jump(stops, reelset);
    }

    public jump(stops: number[], reelset: string[][]) {
        this.currentStops = stops;
        this.currentReelset = reelset;

        this.reelTransition.jump(stops, reelset);

        for (let i = 0; i < stops.length; i++) {
            this.reels[i].jump(stops[i], reelset[i]);
        }

        this.postProcessReels();
    }

    public getReels(): ReelComponent[] {
        return this.reels;
    }

    /**
     * Gets a symbol, (0 indexed)
     * For example getSymbol(4,0) will return the top right symbol in a 5x3 slot
     */
    public getSymbol(x: number, y: number): SymbolComponent {
        if (y < 0 || !this.reels[x] || !this.reels[x].getSymbolAt(y)) {
            // Symbol isn't in "standard" grid, so search hidden symbols
            for (const hiddenSymbol of this.hiddenSymbols) {
                if (hiddenSymbol.gridPosition.x === x + 1 && hiddenSymbol.gridPosition.y === y + 1) {
                    return hiddenSymbol;
                }
            }

            return null;
        }
        return this.reels[x].getSymbolAt(y);
    }

    public getSymbolsFromPositions(positions: Point[]): SymbolComponent[] {
        const symbols = [];

        for (const position of positions) {
            const symbol = Components.matrix.getSymbol(position.x, position.y);
            symbols.push(symbol);
        }

        return symbols;
    }

    /**
     * Returns ALL symbols. Hidden, natural, added sticky - literally all
     */
    public getAllSymbols() {
        return this.getGridSymbols().concat(this.getHiddenSymbols());
    }

    /**
     * Gets all grid symbols, including added sticky symbols
     */
    public getGridSymbols(): SymbolComponent[] {

        const symbols: SymbolComponent[] = this.getBaseGridSymbols();

        if (this.stuckSymbols) {
            this.stuckSymbols.forEach((stuckSymbol) => {
                symbols.push(stuckSymbol);
            });
        }

        return symbols;
    }

    /**
     * Gets this symbols which are not considered part of the win area
     */
    public getHiddenSymbols() {
        return this.hiddenSymbols;
    }

    /**
     * Gets only the symbols on the natural grid, excluding things such as sticky symbols
     */
    public getBaseGridSymbols(): SymbolComponent[] {

        let symbols: SymbolComponent[] = [];

        for (const reel of this.reels) {
            symbols = symbols.concat(reel.getSymbols());
        }

        return symbols;
    }

    public resetSymbolPositions() {
        for (const reel of this.reels) {
            reel.resetSymbolPositions();
        }

        this.postProcessReels();
    }

    public addHiddenSymbol(symbol: SymbolComponent) {
        if (this.hiddenSymbols.indexOf(symbol) === -1) {
            this.hiddenSymbols.push(symbol);
            this.reels[symbol.gridPosition.x - 1].addHiddenSymbol(symbol);
        }
    }

    public resetSymbols() {

        for (const symbol of this.getAllSymbols()) {
            symbol.setVisible(true);
            symbol.static();
        }

        this.postProcessReels();
    }

    public addStuckSymbol(symbol: SymbolComponent) {
        return this.addStuckSymbolById(symbol.gridPosition, symbol.symbolId);
    }

    public addStuckSymbolById(gridPosition: Point, symbolId: string) {
        const id = `symbol_${gridPosition.x}_${gridPosition.y}`;

        if (this.stuckSymbols.has(id)) {
            const existingSticky = this.stuckSymbols.get(id);
            existingSticky.destroy();
        }

        const stuckSymbol = new SymbolComponent(gridPosition.clone(), this.matrixLayer, this.animationLayer);
        if (stuckSymbol.symbolExists(symbolId + "_stuck")) {
            stuckSymbol.setSymbol(symbolId + "_stuck");
        } else {
            stuckSymbol.setSymbol(symbolId);
        }

        const positionRect = this.matrixLayer.getPosition(id);

        stuckSymbol.setTransform(positionRect);
        stuckSymbol.setLayer(this.stickyLayer || this.animationLayer);
        stuckSymbol.lockLayer();
        stuckSymbol.updateTransform();

        this.setDefaultSymbolBehaviours(stuckSymbol);

        this.stuckSymbols.set(id, stuckSymbol);

        this.postProcessReels();

        return stuckSymbol;
    }

    public getStuckSymbol(gridPosition: Point) {
        const id = `symbol_${gridPosition.x}_${gridPosition.y}`;

        return this.stuckSymbols.get(id);
    }

    public getStuckSymbols() {
        return this.stuckSymbols;
    }

    public removeStuckSymbol(gridPosition: Point) {
        const stickySymbol = this.getStuckSymbol(gridPosition);
        const id = `symbol_${gridPosition.x}_${gridPosition.y}`;

        stickySymbol.destroy();
        this.stuckSymbols.delete(id);

        this.resetSymbols();
    }

    /**
     * replaceBase (default false) if true, set the symbol id of the symbol under the sticky symbol to the sticky symbol id
     */
    public clearStuckSymbols(replaceBase: boolean = false) {
        this.stuckSymbols.forEach((symbol: SymbolComponent) => {
            if (replaceBase) {
                const baseSymbol = this.getSymbol(symbol.gridPosition.x - 1, symbol.gridPosition.y - 1);
                baseSymbol.setSymbol(symbol.symbolId);
            }
            symbol.destroy();
        });

        this.stuckSymbols.clear();
        this.resetSymbols();
    }

    public hold(reelIndexes?: number[]) {
        this.reelTransition.hold(reelIndexes);
    }

    public change(scene: string, grid: number[], stops?: number[], reelset?: string[][]) {

        this.matrixLayer.setScene(scene).execute();

        this.checkForOutOfRangeSymbols(grid);

        if (stops) {
            this.defaultStops = this.currentStops = stops;
        } else if (this.currentStops) {
            this.defaultStops = this.currentStops;
        }
        if (reelset) {
            this.defaultReelset = this.currentReelset = reelset;
        } else if (this.currentReelset) {
            this.defaultReelset = this.currentReelset;
        }

        // If resizing, keep existing symbols
        for (let x = 0; x < grid.length; x++) {
            if (this.reels && this.reels[x]) {
                const sizeDiff = grid[x] - this.reels[x].size;
                this.defaultStops[x] -= sizeDiff;
                if (this.defaultStops[x] < 0) {
                    this.defaultStops[x] += this.defaultReelset[x].length;
                }
                if (this.defaultStops[x] >= this.defaultReelset[x].length) {
                    this.defaultStops[x] -= this.defaultReelset[x].length;
                }
            }
        }

        this.clearHiddenSymbols();

        if (this.reels) {
            this.reels.forEach((reel) => reel.destroy());
        }

        // Create reels
        this.reels = [];

        for (let x = 0; x < grid.length; x++) {
            const reelstrip = this.defaultReelset[x];
            const defaultStop: number = this.defaultStops[x];

            const reel = new Components.ReelComponentType(x + 1, grid[x], reelstrip, defaultStop, this.matrixLayer, this.animationLayer);
            this.reels.push(reel);
        }

        // Update transitions if they have already been set
        if (this.reelTransition) {
            this.reelTransition.init(this.reels, this.defaultStops, this.defaultReelset);
        }

        // Add default symbol behaviours if they exist
        for (const symbol of this.getAllSymbols()) {
            this.setDefaultSymbolBehaviours(symbol);
        }

        this.postProcessReels();
    }

    public setCustomReelset(reelset: string[][]) {
        this.getTransition().setCustomReelset(reelset);
    }

    public resetReelsetToDefault() {
        this.getTransition().resetReelsetToDefault();
    }

    public getDefaultReelset() {
        return this.defaultReelset;
    }

    public destroy() {
        this.startSignal.removeAll();
        this.stopSignal.removeAll();
        this.landSignal.removeAll();
        this.onAnticipationStart.removeAll();
        this.onAnticipationEnd.removeAll();
        this.clearStuckSymbols();
        this.clearHiddenSymbols();
        this.hiddenSymbols = null;
        this.reelTransition = null;
        this.reels = null;
        this.defaultStops = null;
        this.defaultReelset = null;
        Timer.clearInterval(this.updateTimer);
    }

    public maskIndividualSymbols() {
        this.matrixLayer.enableBoundsMask(false);

        this.reels.forEach((reel) => reel.maskIndividualSymbols());
    }

    public removeIndividualSymbolsMask() {
        this.matrixLayer.enableBoundsMask(true);

        this.reels.forEach((reel) => reel.removeSymbolMask());
    }

    private checkForOutOfRangeSymbols(grid: number[]) {

        const missingSymbolPositions: string[] = [];

        grid.forEach((maxRows, x) => {
            for (let y = 1; y <= maxRows; y++) {
                const position = this.matrixLayer.getPosition(`symbol_${x + 1}_${y}`);

                if (!position) {
                    missingSymbolPositions.push(`${x + 1}:${y}`);
                }
            }
        });

        if (missingSymbolPositions.length) {
            const listOfSymbols = missingSymbolPositions.join(", ");
            Logger.error(`Out of range symbols detected (${listOfSymbols}), likely due to mismatching gridref layer and matrixGrid definitions.`);
        }
    }

    private clearHiddenSymbols() {
        if (this.reels) {
            this.reels.forEach((reel) => reel.clearHiddenSymbols());
        }
        this.hiddenSymbols = [];
    }

    private onReelLand = (reelIndex: number) => {
        const reel = this.reels[reelIndex - 1];
        reel.land().execute();

        this.landSignal.dispatch(reelIndex);

        this.postProcessReels();
    }

    private update = () => {
        this.reelTransition.update();

        this.postProcessReels();

        this.reelTransition.postUpdate();
    }

    private postProcessReels = () => {
        for (let x = 0; x < this.reels.length; x++) {
            const reel = this.reels[x];

            let currentStack = null;

            for (let y = -2; y < reel.size + 1; y++) {
                const symbol = this.getSymbol(x, y);
                if (!symbol) {
                    continue;
                }
                const symbolDef = SlotDefinition.getSymbolDefinition(symbol.symbolId);

                symbol.offsetStack(0);
                symbol.setIsStackPart(false);
                symbol.setProxy(null);

                if (currentStack && symbol.symbolId !== currentStack.symbolId) {
                    // Found end of stack
                    currentStack = null;
                }

                // Found the top of a stack
                if (!currentStack && symbolDef.size > 1) {
                    currentStack = symbol;
                    const topOfStack = y;

                    // Check if it's the true top or if it's only half in view
                    let visibleSize = 1;
                    for (let rowBelow = y + 1; rowBelow < Math.min(reel.size, y + symbolDef.size); rowBelow++) {
                        const symbolBelow = this.getSymbol(x, rowBelow);

                        if (symbolBelow.symbolId === symbol.symbolId) {
                            // Found more stack
                            visibleSize++;
                            // Hide stack piece and skip processing it
                            symbolBelow.setIsStackPart(true);
                            symbolBelow.setProxy(currentStack);
                            y++;
                        } else {
                            break;
                        }
                    }

                    // If whole thing isn't showing
                    if (visibleSize < symbolDef.size) {
                        // If it's not cut off at the bottom, it means it's cut off at the top, so we offset
                        if (topOfStack + visibleSize < reel.size) {
                            symbol.offsetStack(symbolDef.size - visibleSize);
                        }
                    }
                }

                // Sticky symbols
                this.stuckSymbols.forEach((stuckSymbol: SymbolComponent) => {
                    if (stuckSymbol.gridPosition.x === symbol.gridPosition.x && stuckSymbol.gridPosition.y === symbol.gridPosition.y) {
                        symbol.setVisible(false);
                        symbol.setProxy(stuckSymbol);
                    }
                });

                if (!reel.getVisible()) {
                    symbol.setVisible(false);
                }

                symbol.updateTransform();
            }
        }

        if (this.zSortEnabled) {
            this.zSort();
        }
    }

    private zSort() {
        const orderedSymbols: SymbolComponent[][] = [];

        const allSymbols: SymbolComponent[] = [];

        let reels = [...this.reels];

        // Get symbols sorted according to reel and row, as specified
        if (this.zSortLeftToRight) {
            reels = reels.reverse();
        }

        reels.forEach((reel) => {
            let reelSymbols = [...reel.getSymbols()];
            if (this.zSortTopToBottom) {
                reelSymbols = reelSymbols.reverse();
            }
            reelSymbols.forEach((symbol) => {
                allSymbols.push(symbol);
            });
        });

        // Create z-index array
        allSymbols.forEach((symbol) => {
            const zIndex = symbol.symbolDefinition.zIndex || 0;
            if (!orderedSymbols[zIndex]) {
                orderedSymbols[zIndex] = [];
            }

            orderedSymbols[zIndex].push(symbol);
        });

        // Order symbols by z-index array
        orderedSymbols.forEach((symbols) => {
            if (symbols && symbols.length) {
                symbols.forEach((symbol) => {
                    symbol.moveToTop();
                });
            }
        });
    }

    private setDefaultSymbolBehaviours(symbol: SymbolComponent) {
        for (const defaultSymbolBehaviour of this.defaultSymbolBehaviourTypes) {
            const behaviour = new defaultSymbolBehaviour.behaviourType(symbol);
            behaviour.validSymbols = defaultSymbolBehaviour.filteredSymbols;

            symbol.addBehaviour(behaviour);
            symbol.refresh();
        }
    }
}

export default MatrixComponent;
